/* eslint-disable react-refresh/only-export-components */
import {
  DimeButton,
  DimeDialog,
  DimeDialogClose,
  DimeDialogContent,
  DimeDialogDescription,
  DimeDialogFooter,
  DimeDialogHeader,
  DimeDialogTitle,
  DimeDialogTrigger,
  DimeField,
  DimeFieldLabel,
  DimeInput,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/dialog')({
  component: DialogDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-4 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function BasicExample() {
  return (
    <DimeDialog>
      <DimeDialogTrigger asChild>
        <DimeButton variant="outline">Edit profile</DimeButton>
      </DimeDialogTrigger>
      <DimeDialogContent>
        <DimeDialogHeader>
          <DimeDialogTitle>Edit profile</DimeDialogTitle>
          <DimeDialogDescription>
            Make changes to your profile here. Click save when you're done.
          </DimeDialogDescription>
        </DimeDialogHeader>
        <div className="space-y-3 py-2">
          <DimeField>
            <DimeFieldLabel htmlFor="dlg-name">Name</DimeFieldLabel>
            <DimeInput id="dlg-name" defaultValue="Devon Park" />
          </DimeField>
          <DimeField>
            <DimeFieldLabel htmlFor="dlg-username">Username</DimeFieldLabel>
            <DimeInput id="dlg-username" defaultValue="@devon" />
          </DimeField>
        </div>
        <DimeDialogFooter showCloseButton>
          <DimeButton>Save changes</DimeButton>
        </DimeDialogFooter>
      </DimeDialogContent>
    </DimeDialog>
  );
}

function NoCloseButtonExample() {
  return (
    <DimeDialog>
      <DimeDialogTrigger asChild>
        <DimeButton variant="outline">Open without X</DimeButton>
      </DimeDialogTrigger>
      <DimeDialogContent showCloseButton={false}>
        <DimeDialogHeader>
          <DimeDialogTitle>Confirm action</DimeDialogTitle>
          <DimeDialogDescription>
            This action cannot be undone. Are you sure you want to continue?
          </DimeDialogDescription>
        </DimeDialogHeader>
        <DimeDialogFooter>
          <DimeDialogClose asChild>
            <DimeButton variant="outline" size="sm">
              Cancel
            </DimeButton>
          </DimeDialogClose>
          <DimeButton variant="destructive" size="sm">
            Delete
          </DimeButton>
        </DimeDialogFooter>
      </DimeDialogContent>
    </DimeDialog>
  );
}

function DialogDocs() {
  return (
    <div className="max-w-3xl">
      <h1 className="mb-1 text-3xl font-bold">Dialog</h1>
      <p className="mb-8 text-muted-foreground">
        A modal dialog built on Radix UI. Compose{' '}
        <code className="font-mono text-sm">DimeDialogTrigger</code>,{' '}
        <code className="font-mono text-sm">DimeDialogContent</code>,{' '}
        <code className="font-mono text-sm">DimeDialogHeader</code>,{' '}
        <code className="font-mono text-sm">DimeDialogFooter</code>,{' '}
        <code className="font-mono text-sm">DimeDialogTitle</code>, and{' '}
        <code className="font-mono text-sm">DimeDialogDescription</code> inside{' '}
        <code className="font-mono text-sm">DimeDialog</code>.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <BasicExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Wrap your trigger with <code className="font-mono">DimeDialogTrigger asChild</code>. The
          content renders in a portal with a backdrop. A close button (×) is shown by default — pass{' '}
          <code className="font-mono">showCloseButton</code> to{' '}
          <code className="font-mono">DimeDialogFooter</code> to also add a Close button there.
        </p>
      </DocsSection>

      <DocsSection title="Confirmation">
        <Preview>
          <NoCloseButtonExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">showCloseButton={'{false}'}</code> to{' '}
          <code className="font-mono">DimeDialogContent</code> to hide the × button. Use{' '}
          <code className="font-mono">DimeDialogClose asChild</code> to wire your own cancel button.
        </p>
      </DocsSection>

      <DocsSection title="Full screen on phones">
        <p className="text-sm text-muted-foreground">
          Pass <code className="font-mono">fullScreenOnPhone</code> to{' '}
          <code className="font-mono">DimeDialogContent</code> for touch work such as picking: below
          the <code className="font-mono">sm</code> breakpoint it fills the screen, and at any width
          it lays out as a column so a body with{' '}
          <code className="font-mono">flex-1 overflow-y-auto</code> scrolls between the header and
          footer. Set the wider width yourself (e.g.{' '}
          <code className="font-mono">sm:max-w-[720px]</code>).
        </p>
      </DocsSection>
    </div>
  );
}
